import type { ReactNode } from 'react';

import { cn } from '@/lib/cn';

export interface PageHeaderProps {
  title: ReactNode;
  sub?: ReactNode;
  actions?: ReactNode;
  className?: string | undefined;
}

export function PageHeader({ title, sub, actions, className }: PageHeaderProps) {
  return (
    <div className={cn('mb-4 flex flex-wrap items-start gap-3', className)}>
      <div className="min-w-0">
        <h1 className="page-title font-cond text-[23px] leading-tight font-extrabold tracking-[.005em]">
          {title}
        </h1>
        {sub ? <div className="mt-0.5 text-ink3">{sub}</div> : null}
      </div>
      {actions ? (
        <div className="ph-actions ml-auto flex flex-wrap items-center gap-2">{actions}</div>
      ) : null}
    </div>
  );
}

export function FilterBar({
  children,
  right,
  className,
}: {
  children: ReactNode;
  right?: ReactNode;
  className?: string | undefined;
}) {
  return (
    <div className={cn('mb-3.5 flex flex-wrap items-center gap-2', className)}>
      {children}
      {right ? <div className="ml-auto text-xs text-ink3 num">{right}</div> : null}
    </div>
  );
}

export function EmptyState({ children }: { children: ReactNode }) {
  return <div className="p-7 text-center text-ink3">{children}</div>;
}

export function LoadingState({ label = 'Loading…' }: { label?: string | undefined }) {
  return (
    <div className="p-7 text-center text-ink3" role="status">
      {label}
    </div>
  );
}

export function ErrorState({
  error,
  onRetry,
}: {
  error: unknown;
  onRetry?: (() => void) | undefined;
}) {
  const message = error instanceof Error ? error.message : 'Something went wrong';
  return (
    <div className="rounded-xl border border-bad bg-hi-bad p-4 text-[13px]" role="alert">
      <b className="text-bad">Could not load.</b> {message}
      {onRetry ? (
        <button
          type="button"
          className="ml-2 font-semibold text-orange-ink underline"
          onClick={onRetry}
        >
          Retry
        </button>
      ) : null}
    </div>
  );
}
